import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import { Line } from 'react-chartjs-2';
import { Alert, Card, CardBody, Checkbox, Input } from '@heroui/react';
import { FaInfoCircle } from 'react-icons/fa';
import {
  faBaby,
  faBedPulse,
  faCalendarCheck,
  faChartLine,
  faFileArrowDown,
  faFilePdf,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Footer from '../../../components/Footer';
import FooterItem from '../../../components/FooterItem';
import AppsHeader from '../../components/AppsHeader';
import type { Aap2022Settings, ProtocolFile } from '../phototherapyConfig';
import { protocolFileDownloadName, protocolFileHref } from '../protocolFiles';
import { escalationLevel, evaluateAap, type AapEvaluation, type AapStatus, type MeasurementType } from './aapCalculation';
import { AAP_MAX_HOURS, AAP_MIN_HOURS, getAapCurve, type AapThresholdKind } from './aapThresholds';
import '../phototherapy.scss';

Chart.register(...registerables);

type DataShown = 'CALCULATOR' | 'RISKS' | 'GRAPHS' | 'FOLLOW_UP' | 'PROTOCOLS';

const GA_OPTIONS: { value: number; label: string }[] = [
  { value: 35, label: '35' },
  { value: 36, label: '36' },
  { value: 37, label: '37' },
  { value: 38, label: '38' },
  { value: 39, label: '39' },
  { value: 40, label: '40+' },
];

/** The app-wide text colour (--page-font-color in Apps.scss). Everything on this
 *  screen is written in it except the warnings, which keep their alert colour. */
const PAGE_FONT_COLOR = '#103C6E';

/**
 * The label column. `clamp` rather than a fixed 100px because the row beside it
 * has to hold six gestational-age buttons: on a phone the column gives width
 * back so the card still fits the viewport instead of being clipped at the edge.
 */
const LABEL_COLUMN_WIDTH = 'clamp(72px, 22vw, 100px)';

const labelStyle = {
  width: LABEL_COLUMN_WIDTH,
  minWidth: LABEL_COLUMN_WIDTH,
  fontSize: 'clamp(0.8rem, 3.2vw, 0.97rem)',
  textAlign: 'right' as const,
  color: PAGE_FONT_COLOR,
  alignSelf: 'center',
  // .grid-label is display:flex, so a label carrying its unit as a second word
  // would lay both out on one line and push past the column. Wrapping lets the
  // bilirubin label take two lines instead of widening the column.
  flexWrap: 'wrap' as const,
  columnGap: '4px',
};

const inputClassNames = {
  inputWrapper: 'bg-white border rounded-sm hover:border-blue-600 focus-within:border-blue-700 border-blue-500',
  input: 'bg-white text-right',
};

const invalidInputClassNames = {
  inputWrapper: 'bg-white border rounded-sm border-red-600 focus-within:border-red-700',
  input: 'bg-white text-right',
};

const segmentStyle = (checked: boolean): React.CSSProperties => ({
  flex: 1,
  // Lets the six gestational-age buttons share whatever the label column leaves,
  // instead of their text forcing the table wider than the card.
  minWidth: 0,
  minHeight: '40px',
  borderRadius: '8px',
  // Same colour as the label it sits beside, checked or not — an unselected
  // button is the same control, only quieter, so only its weight changes.
  border: `${checked ? 2 : 1}px solid ${PAGE_FONT_COLOR}`,
  background: checked ? PAGE_FONT_COLOR : 'white',
  color: checked ? '#FFFFFF' : PAGE_FONT_COLOR,
  fontSize: 'clamp(0.68rem, 3.2vw, 0.95rem)',
  fontWeight: 500,
  cursor: 'pointer',
  // "40+" and "TcB (מלעורי)" are single tokens; letting them wrap turned the
  // narrowest buttons into two stacked lines.
  whiteSpace: 'nowrap',
  padding: '0 2px',
  transition: 'background 120ms, color 120ms, border-color 120ms',
});

/**
 * The line that opens the risk-factor screen. The whole sentence is the control,
 * not just the icon beside it, and it stays on one line at any width — the text
 * scales instead of wrapping, because wrapping it pushed the card past the edge
 * of a phone screen.
 */
const riskLinkStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  // The sentence never breaks across lines; on a screen too narrow to hold it
  // beside the checkbox the whole control drops below instead (see the row's
  // flexWrap), where it has the full column to itself.
  whiteSpace: 'nowrap',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  color: PAGE_FONT_COLOR,
  fontSize: 'clamp(0.7rem, 2.9vw, 0.95rem)',
  textAlign: 'right',
};

/** How close to a curve the pointer has to get before its value pops up.
 *  The curves are drawn with no visible markers and one datum per hour, so
 *  without this the hit area is the default 1px and practically unhittable. */
const HOVER_RADIUS_PX = 14;

/**
 * Monotone cubic interpolation: unlike `tension` it provably never overshoots a
 * data point, so a drawn line can never bulge past the values it connects.
 */
const SMOOTH_LINE = { cubicInterpolationMode: 'monotone' as const };

/**
 * How far a drawn point may sit from its transcribed value, in mg/dL.
 * 0.1 is the resolution of the dataset itself: the AAP publishes these as smooth
 * curves and we hold them as one number per hour rounded to a tenth, so the
 * staircase on screen is the rounding, not the guideline. Staying inside one
 * rounding step means the line can only ever be drawn through values the
 * transcription cannot distinguish from the ones it stores.
 */
const SMOOTH_TOLERANCE_MG_DL = 0.1;

/** Widest half-window the filter may average over, in hours. */
const SMOOTH_MAX_RADIUS_HOURS = 12;

interface CurvePoint {
  x: number;
  y: number;
  /** The transcribed value at this hour — what the tooltip reports. */
  exact: number;
}

/**
 * Turns an hourly curve into chart points, smoothing the rounding staircase out
 * of the *picture* without letting the picture drift from the data.
 *
 * Each hour is drawn at the mean of the widest centred window whose values span
 * no more than SMOOTH_TOLERANCE_MG_DL. Every curve here is non-decreasing (a
 * dataset invariant pinned in `aapThresholds.test.ts`), so that mean always lies
 * between the window's ends and therefore within the tolerance of the hour's own
 * value. The window adapts on its own: across a plateau, where a tenth is spread
 * over a dozen hours, it opens up and pulls the steps into a line; through the
 * steep first day, where consecutive hours already differ by more than a tenth,
 * it collapses to a single hour and nothing is changed at all.
 *
 * `exact` carries the untouched value through to the tooltip, so hovering the
 * line still reads the transcription — and no clinical decision comes through
 * here in the first place: `evaluateAap` reads the dataset directly.
 */
function smoothCurvePoints(values: readonly number[]): CurvePoint[] {
  const n = values.length;
  const points: CurvePoint[] = [];
  for (let i = 0; i < n; i++) {
    let radius = 0;
    while (
      radius < SMOOTH_MAX_RADIUS_HOURS &&
      i - (radius + 1) >= 0 &&
      i + (radius + 1) < n &&
      values[i + radius + 1] - values[i - radius - 1] <= SMOOTH_TOLERANCE_MG_DL
    ) {
      radius++;
    }
    let sum = 0;
    for (let k = i - radius; k <= i + radius; k++) sum += values[k];
    points.push({ x: i + 1, y: sum / (2 * radius + 1), exact: values[i] });
  }
  return points;
}

/** The transcribed value behind a hovered point, falling back to the drawn one. */
const tooltipValue = (ctx: any): number =>
  typeof ctx.raw?.exact === 'number' ? ctx.raw.exact : ctx.parsed.y;

const fmt = (v: number) => Number(v.toFixed(1)).toString();
const gaLabel = (week: number) => (week >= 40 ? '40+' : String(week));

const HEADLINES: Record<AapStatus, { text: string; color: 'danger' | 'success' | 'warning' }> = {
  exchange: { text: 'עובר את סף החלפת דם - נדרש עירוי חליפין דחוף', color: 'danger' },
  escalation: { text: 'נדרשת הסלמת טיפול - ערך בילירובין בסמיכות לסף החלפת דם', color: 'danger' },
  phototherapy: { text: 'נדרש טיפול באור', color: 'danger' },
  subthreshold: { text: 'לא נדרש טיפול באור', color: 'success' },
  below: { text: 'לא נדרש טיפול באור', color: 'success' },
};

interface Props {
  settings: Aap2022Settings;
  /** The department's documents. An empty list hides the "פרוטוקולים" screen. */
  protocolFiles?: ProtocolFile[];
  /**
   * The name of the app this calculator is mounted under, for the breadcrumb —
   * two apps share this screen (see `../variants.ts`). The header below names the
   * calculator, which is the same in both.
   */
  pageTitle?: string;
}

const PhototherapyAap2022: React.FC<Props> = ({
  settings,
  protocolFiles = [],
  pageTitle = 'צהבת ביילוד',
}) => {
  const [gaWeek, setGaWeek] = useState<number>(40);
  const [hasRisk, setHasRisk] = useState<boolean>(false);
  const [measurement, setMeasurement] = useState<MeasurementType>('TSB');
  const [bilirubin, setBilirubin] = useState<string>('');
  const [ageInHours, setAgeInHours] = useState<string>('');
  const [evaluation, setEvaluation] = useState<AapEvaluation | null>(null);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(true);
  const [dataShown, setDataShown] = useState<DataShown>('CALCULATOR');
  const chartsContainerRef = useRef<HTMLDivElement>(null);

  // A reference whose file cannot be resolved (a built-in key this version of
  // the app does not know) is dropped here rather than rendered as a dead tile —
  // and if that leaves nothing, the footer item goes too, exactly as it does for
  // a hospital that uploaded no documents at all.
  const openableProtocolFiles = useMemo(
    () => protocolFiles.filter((f) => protocolFileHref(f) !== undefined),
    [protocolFiles]
  );
  const hasProtocolFiles = openableProtocolFiles.length > 0;

  const bilirubinNum = parseFloat(bilirubin.replace(',', '.'));
  const ageInHoursNum = parseInt(ageInHours, 10);
  // The AAP curves stop at 336 h (14 days), so an age past the last hour of the
  // guideline is refused outright rather than answered off the hour-336 plateau.
  const ageInRange =
    !isNaN(ageInHoursNum) && ageInHoursNum >= AAP_MIN_HOURS && ageInHoursNum <= AAP_MAX_HOURS;
  const ageOutOfRange = ageInHours !== '' && !ageInRange;
  const allInputsSatisfied = ageInRange && !isNaN(bilirubinNum) && bilirubinNum > 0;

  const calculate = useCallback(() => {
    if (!allInputsSatisfied) {
      setEvaluation(null);
      setIsCollapsed(true);
      return;
    }
    setEvaluation(
      evaluateAap(
        {
          gestationalAgeWeeks: gaWeek,
          ageHours: ageInHoursNum,
          bilirubinMgDl: bilirubinNum,
          hasNeurotoxicityRisk: hasRisk,
          measurement,
        },
        settings
      )
    );
  }, [allInputsSatisfied, gaWeek, ageInHoursNum, bilirubinNum, hasRisk, measurement, settings]);

  // Same debounce as the classic calculator: let the user finish a two-digit age.
  useEffect(() => {
    const handler = setTimeout(calculate, ageInHoursNum <= 9 && ageInHours.length === 1 ? 800 : 20);
    return () => clearTimeout(handler);
  }, [calculate, ageInHours, ageInHoursNum]);

  useEffect(() => {
    if (!isCollapsed && evaluation) {
      const timer = setTimeout(() => {
        chartsContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isCollapsed, evaluation]);

  const resetAll = () => {
    setGaWeek(40);
    setHasRisk(false);
    setMeasurement('TSB');
    setBilirubin('');
    setAgeInHours('');
    setEvaluation(null);
    setIsCollapsed(true);
    setDataShown('CALCULATOR');
  };

  const handleBilirubinChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[0-9]{0,2}([.,][0-9]{0,1})?$/.test(value) || value === '') {
      if (
        parseFloat(value.replace(',', '.')) <= 50 ||
        value === '' ||
        value.endsWith('.') ||
        value.endsWith(',')
      ) {
        setBilirubin(value);
      }
    }
  }, []);

  const handleAgeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[0-9]{0,3}$/.test(value) || value === '') {
      setAgeInHours(value);
    }
  }, []);

  // --- patient chart (three thresholds for the selected case + the patient's point) ---
  const patientChartData = useMemo(() => {
    const phototherapyCurve = getAapCurve(gaWeek, hasRisk, 'phototherapy');
    const exchangeCurve = getAapCurve(gaWeek, hasRisk, 'exchange');
    // The escalation line is not a published curve: it is the exchange curve
    // shifted down by the hospital's configured delta, read through the same
    // helper the status ladder uses so the graph cannot disagree with the result.
    // Both inputs are non-decreasing and so is the maximum of them, which is what
    // smoothCurvePoints needs.
    const escalationCurve = Array.from(exchangeCurve, (exchange, i) =>
      escalationLevel({ phototherapy: phototherapyCurve[i], exchange }, settings.escalationDeltaMgDl)
    );
    const datasets: any[] = [
      { data: smoothCurvePoints(phototherapyCurve), borderColor: 'black', label: 'טיפול באור', pointRadius: 0, pointHitRadius: HOVER_RADIUS_PX, borderWidth: 2, ...SMOOTH_LINE },
      { data: smoothCurvePoints(escalationCurve), borderColor: '#d97706', label: 'הסלמת טיפול', pointRadius: 0, pointHitRadius: HOVER_RADIUS_PX, borderWidth: 2, borderDash: [6, 4], ...SMOOTH_LINE },
      { data: smoothCurvePoints(exchangeCurve), borderColor: 'red', label: 'החלפת דם', pointRadius: 0, pointHitRadius: HOVER_RADIUS_PX, borderWidth: 2, ...SMOOTH_LINE },
    ];
    if (allInputsSatisfied) {
      datasets.push({
        data: [{ x: Math.min(ageInHoursNum, AAP_MAX_HOURS), y: bilirubinNum }],
        backgroundColor: 'red',
        borderColor: 'red',
        pointRadius: 4,
        pointHoverRadius: 6,
        pointHitRadius: HOVER_RADIUS_PX,
        showLine: false,
        label: 'הערך הנוכחי',
      });
    }
    return { datasets };
  }, [gaWeek, hasRisk, allInputsSatisfied, ageInHoursNum, bilirubinNum, settings.escalationDeltaMgDl]);

  const xMax = allInputsSatisfied && ageInHoursNum > 168 ? AAP_MAX_HOURS : 168;

  const patientChartOptions = useMemo(
    () => ({
      responsive: true,
      aspectRatio: 1,
      // 'nearest' + the enlarged pointHitRadius: anywhere within HOVER_RADIUS_PX
      // of a curve reads that curve, and only the closest one - so a wide hit
      // area does not turn into a tooltip listing every hour it touched.
      interaction: { mode: 'nearest' as const, intersect: true },
      plugins: {
        legend: { display: true, position: 'top' as const, labels: { usePointStyle: true, pointStyle: 'line' } },
        tooltip: {
          rtl: true,
          textDirection: 'rtl' as const,
          mode: 'nearest' as const,
          intersect: true,
          callbacks: {
            title: () => '',
            label: (ctx: any) =>
              ctx.parsed.y === null
                ? ''
                : `${ctx.dataset.label}: בילירובין ${tooltipValue(ctx).toFixed(1)}, גיל ${ctx.parsed.x}`,
          },
        },
        title: {
          display: true,
          text: `עקומות AAP 2022 - שבוע ${gaLabel(gaWeek)} ${hasRisk ? 'עם' : 'ללא'} גורמי סיכון`,
        },
      },
      scales: {
        x: { type: 'linear' as const, title: { display: true, text: 'גיל בשעות' }, ticks: { stepSize: 24 }, min: 0, max: xMax },
        y: { type: 'linear' as const, title: { display: true, text: 'בילירובין (mg/dl)' }, beginAtZero: true, ticks: { stepSize: 2 }, max: 30 },
      },
    }),
    [gaWeek, hasRisk, xMax]
  );

  const headline = evaluation ? HEADLINES[evaluation.status] : null;
  const isTreating =
    evaluation !== null && (evaluation.status === 'phototherapy' || evaluation.status === 'escalation' || evaluation.status === 'exchange');
  const isEscalating = evaluation !== null && (evaluation.status === 'escalation' || evaluation.status === 'exchange');
  const isBelow = evaluation !== null && (evaluation.status === 'subthreshold' || evaluation.status === 'below');

  return (
    <div className="app-with-footer">
      <div className="container main-content phototherapy-main-content" data-page-title={pageTitle}>
        {dataShown === 'CALCULATOR' && (
          <div id="calculator">
            <AppsHeader title="צהבת ביילוד" subsection="AAP 2022" />
            <Card
              radius="sm"
              className="w-full"
              style={{
                borderRadius: 'var(--radius-m, 8px)',
                background: 'var(--neutral-light, #EEFFFD)',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)',
              }}
            >
              <CardBody className="space-y-4">
                <div style={{ width: '100%', maxWidth: '420px', marginInline: 'auto' }}>
                  {/* `fixed` so the label column keeps the width it asks for and the
                      rest of each row is made to fit it — with `auto`, the six
                      gestational-age buttons widen the table past the card on a phone. */}
                  <table style={{ width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: '0' }}>
                    <tbody>
                      {/* Gestational age */}
                      <tr>
                        <td style={{ width: LABEL_COLUMN_WIDTH, direction: 'rtl', verticalAlign: 'middle' }}>
                          <label data-testid="ga-week-select" className="grid-label" style={labelStyle}>
                            שבוע הריון
                          </label>
                        </td>
                        <td style={{ direction: 'rtl' }} colSpan={3}>
                          <div role="radiogroup" aria-label="gestational-age" style={{ display: 'flex', gap: 'clamp(3px, 1.6vw, 6px)', width: '100%' }}>
                            {GA_OPTIONS.map((opt) => {
                              const checked = gaWeek === opt.value;
                              return (
                                <button
                                  key={opt.value}
                                  type="button"
                                  role="radio"
                                  aria-checked={checked}
                                  data-testid={`ga-week-${opt.value}`}
                                  onClick={() => setGaWeek(opt.value)}
                                  style={segmentStyle(checked)}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                      <tr><td colSpan={4} style={{ height: '12px' }} /></tr>

                      {/* Neurotoxicity risk factors */}
                      <tr>
                        <td style={{ direction: 'rtl', verticalAlign: 'middle' }}>
                          <label className="grid-label" style={labelStyle}>גורם סיכון</label>
                        </td>
                        <td style={{ direction: 'rtl' }} colSpan={3}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <Checkbox data-testid="risk-factor-risky" isSelected={hasRisk} onValueChange={setHasRisk} />
                            <button
                              type="button"
                              data-testid="risk-factors-link"
                              style={riskLinkStyle}
                              onClick={() => setDataShown('RISKS')}
                            >
                              <FaInfoCircle style={{ flexShrink: 0, verticalAlign: 'middle' }} aria-hidden="true" />
                              <span>לפירוט גורמי סיכון לנוירוטוקסיות</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr><td colSpan={4} style={{ height: '12px' }} /></tr>

                      {/* Measurement type */}
                      <tr>
                        <td style={{ direction: 'rtl', verticalAlign: 'middle' }}>
                          <label className="grid-label" style={labelStyle}>סוג מדידה</label>
                        </td>
                        <td style={{ direction: 'rtl' }} colSpan={3}>
                          <div role="radiogroup" aria-label="measurement" style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            {(
                              [
                                { key: 'TSB', label: 'TSB (דם)' },
                                { key: 'TcB', label: 'TcB (מלעורי)' },
                              ] as const
                            ).map((opt) => {
                              const checked = measurement === opt.key;
                              return (
                                <button
                                  key={opt.key}
                                  type="button"
                                  role="radio"
                                  aria-checked={checked}
                                  data-testid={`measurement-${opt.key}`}
                                  onClick={() => setMeasurement(opt.key)}
                                  style={segmentStyle(checked)}
                                >
                                  {opt.label}
                                </button>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                      <tr><td colSpan={4} style={{ height: '12px' }} /></tr>

                      {/* Bilirubin */}
                      <tr>
                        <td style={{ direction: 'rtl', verticalAlign: 'middle' }}>
                          <label className="grid-label" style={labelStyle}>
                            <span>בילירובין</span>
                            <span style={{ whiteSpace: 'nowrap' }}>(מ"ג/ד"ל)</span>
                          </label>
                        </td>
                        <td style={{ direction: 'rtl' }} colSpan={3}>
                          <Input
                            id="bilirubin"
                            placeholder="mg/dl"
                            type="text"
                            inputMode="decimal"
                            pattern="[0-9]{0,2}[.,]?[0-9]{0,1}"
                            value={bilirubin}
                            onChange={handleBilirubinChange}
                            onFocus={(e) => e.target.select()}
                            classNames={inputClassNames}
                          />
                        </td>
                      </tr>
                      <tr><td colSpan={4} style={{ height: '12px' }} /></tr>

                      {/* Age */}
                      <tr>
                        <td style={{ direction: 'rtl', verticalAlign: 'middle' }}>
                          <label className="grid-label" style={labelStyle}>גיל (בשעות)</label>
                        </td>
                        <td style={{ direction: 'rtl' }} colSpan={3}>
                          <Input
                            id="ageInHours"
                            placeholder={`${AAP_MIN_HOURS}-${AAP_MAX_HOURS}`}
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]{0,3}"
                            value={ageInHours}
                            onChange={handleAgeChange}
                            onFocus={(e) => e.target.select()}
                            aria-invalid={ageOutOfRange}
                            classNames={ageOutOfRange ? invalidInputClassNames : inputClassNames}
                          />
                          {ageOutOfRange && (
                            <div
                              id="age-range-error"
                              data-testid="age-range-error"
                              className="text-danger"
                              style={{
                                fontSize: '0.85rem',
                                marginTop: '4px',
                                // The page centres its text, so the message needs
                                // saying: it belongs under the right-hand edge of
                                // the field it is about, like every other RTL line.
                                direction: 'rtl',
                                textAlign: 'right',
                              }}
                            >
                              {`יש להזין ערך בין ${AAP_MIN_HOURS}-${AAP_MAX_HOURS}`}
                            </div>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardBody>
            </Card>

            {evaluation && headline && (
              <div id="results-container" className="my-4 max-w-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Alert
                    className="flex-1"
                    hideIcon
                    color={headline.color}
                    style={{ backgroundColor: headline.color === 'success' ? '#E7FFDA' : undefined }}
                  >
                    <span id="root-diagnose" style={{ color: PAGE_FONT_COLOR }}>{headline.text}</span>
                  </Alert>
                  <button
                    onClick={resetAll}
                    className="p-1 hover:opacity-70 transition-opacity flex-shrink-0"
                    aria-label="Reset"
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 4.00001V10M1 10H7M1 10L5.64 5.64001C6.71475 4.56473 8.04437 3.77922 9.50481 3.35679C10.9652 2.93436 12.5089 2.88877 13.9917 3.22427C15.4745 3.55978 16.8482 4.26545 17.9845 5.27543C19.1209 6.28542 19.9828 7.5668 20.49 9.00001M23 20V14M23 14H17M23 14L18.36 18.36C17.2853 19.4353 15.9556 20.2208 14.4952 20.6432C13.0348 21.0657 11.4911 21.1113 10.0083 20.7758C8.52547 20.4402 7.1518 19.7346 6.01547 18.7246C4.87913 17.7146 4.01717 16.4332 3.51 15" stroke="#103C6E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </div>

                <div className="aap-results">
                  {evaluation.status === 'subthreshold' && (
                    <h5 id="light-alert-message" className="card-title text-warning">
                      ערך בילירובין מתקרב לסף טיפול באור ({fmt(evaluation.deltaToPhototherapy)} מ"ג/ד"ל מתחת לסף) - ניתן לשקול טיפול באור באשפוז
                    </h5>
                  )}

                  {evaluation.tcbNeedsTsb && (
                    <h5 id="tcb-warning" className="card-title text-warning">
                      ערך TcB בטווח של {fmt(settings.tcbToTsbDeltaMgDl)} מ"ג/ד"ל מסף הטיפול או ≥{fmt(settings.tcbToTsbAbsoluteMgDl)} - נדרשת מדידת TSB בדם לפני קבלת החלטה
                    </h5>
                  )}

                  {/* Procedure §2. Skipped while treating: the lab panel below already lists it. */}
                  {evaluation.bloodTypeDatNeeded && !isTreating && (
                    <p id="blood-type-dat" className="aap-note">
                      ערך הבילירובין בטווח {fmt(settings.bloodTypeDatDeltaMgDl as number)} מ"ג/ד"ל מסף הטיפול
                      לילודים עם גורמי סיכון לנוירוטוקסיות ({fmt(evaluation.withRiskPhototherapyThreshold)} מ"ג/ד"ל
                      לגיל זה) - יש לקחת דגימה לסוג דם ו-DAT.
                    </p>
                  )}

                  <p id="thresholds-info" style={{ fontSize: '1rem', marginTop: '8px' }}>
                    עבור יילוד בשבוע {gaLabel(gaWeek)} בגיל {ageInHoursNum} שעות {hasRisk ? 'עם' : 'ללא'} גורמי סיכון:
                    סף טיפול באור <strong>{fmt(evaluation.thresholds.phototherapy)}</strong>,
                    סף הסלמת טיפול <strong>{fmt(evaluation.escalationThreshold)}</strong>,
                    סף החלפת דם <strong>{fmt(evaluation.thresholds.exchange)}</strong> מ"ג/ד"ל.
                  </p>

                  {/* No out-of-range note here: the age field refuses anything past
                      AAP_MAX_HOURS, so `outOfGuidelineRange` can never be true for a
                      result this screen renders. `evaluateAap` still reports it for
                      other callers. */}

                  {isEscalating && (
                    <div id="consider-transfusion" className="aap-escalation">
                      <div className="card-title">
                        {evaluation.status === 'exchange'
                          ? 'ערך TSB בסף החלפת דם או מעליו - עירוי חליפין דחוף'
                          : `ערך TSB בטווח של ${fmt(settings.escalationDeltaMgDl)} מ"ג/ד"ל מסף החלפת דם (${fmt(evaluation.deltaToExchange)} מתחת לסף)`}
                      </div>
                      <ul>
                        {settings.escalationSteps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {isTreating && (
                    <div className="card-text mt-3 follow-up-info">
                      <p id="stop-phototherapy">
                        ניתן להפסיק טיפול באור כאשר ערך הבילירובין יורד ב-<strong>{fmt(settings.stopPhototherapyDeltaMgDl)}</strong> מ"ג/ד"ל מתחת לסף.
                      </p>
                      {settings.phototherapyStartLabs.length > 0 && (
                        <>
                          <strong>בדיקות עם תחילת טיפול באור:</strong>
                          <ul>
                            {settings.phototherapyStartLabs.map((lab, i) => (
                              <li key={i}>{lab}</li>
                            ))}
                          </ul>
                        </>
                      )}
                      <button type="button" className="btn btn-link" style={{ padding: 0 }} onClick={() => setDataShown('FOLLOW_UP')}>
                        מעקב במהלך טיפול באור ולאחריו
                      </button>
                    </div>
                  )}

                  {isBelow && (
                    <div className="card-text mt-3 follow-up-info">
                      {evaluation.before12h ? (
                        <p id="follow-up-text">{settings.followUpBefore12hText}</p>
                      ) : evaluation.followUp ? (
                        <p id="follow-up-text">
                          <strong>מעקב לאחר שחרור</strong> (ההפרש מסף טיפול באור: {fmt(evaluation.deltaToPhototherapy)} מ"ג/ד"ל):{' '}
                          {evaluation.followUp.recommendation}
                        </p>
                      ) : null}
                      {evaluation.prolongedJaundice && settings.prolongedJaundiceFromHours !== null && (
                        <p id="prolonged-jaundice" className="text-warning">
                          צהבת ממושכת (גיל ≥{Math.round(settings.prolongedJaundiceFromHours / 24)} ימים ובילירובין בטווח {fmt(settings.prolongedJaundiceDeltaMgDl)} מ"ג/ד"ל מהסף) - יש לשלול כולסטזיס (בילירובין ישיר) ולהעריך הזנה ומגמת משקל.
                        </p>
                      )}
                    </div>
                  )}

                  {evaluation.hearingTestReason !== null && (
                    <p id="hearing-test" className="card-text mt-2 follow-up-info">
                      {evaluation.hearingTestReason === 'level'
                        ? `ערך בילירובין ${fmt(settings.hearingTestTsbMgDl as number)} מ"ג/ד"ל ומעלה - `
                        : 'ערך בילירובין המצריך הסלמת טיפול - '}
                      יש להמליץ על בדיקת שמיעה (BERA) במכון שמיעה לאחר השחרור. ההמלצה חלה גם על יילוד שהציג
                      תסמינים קליניים החשודים לאנצפלופתיה חריפה מבילירובין (ABE).
                    </p>
                  )}

                  <div className="mt-3">
                    <button type="button" className="btn btn-link" style={{ padding: '0px' }} onClick={() => setIsCollapsed(!isCollapsed)}>
                      {isCollapsed ? 'צפה בערך על העקומות' : 'סגור תצוגה גרפית'}
                    </button>
                  </div>
                  {!isCollapsed && (
                    <div ref={chartsContainerRef} className="charts-container mt-3">
                      <Line options={patientChartOptions} data={patientChartData} />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {dataShown === 'RISKS' && <RisksView settings={settings} />}
        {dataShown === 'GRAPHS' && <GraphsView settings={settings} />}
        {dataShown === 'FOLLOW_UP' && <FollowUpView settings={settings} />}
        {dataShown === 'PROTOCOLS' && hasProtocolFiles && <ProtocolsView files={openableProtocolFiles} />}
      </div>

      <Footer>
        <FooterItem label="מחשבון צהבת" icon={faBaby} onClick={() => setDataShown('CALCULATOR')} isActive={dataShown === 'CALCULATOR'} />
        <FooterItem label="גורמי סיכון" icon={faBedPulse} onClick={() => setDataShown('RISKS')} isActive={dataShown === 'RISKS'} />
        <FooterItem label="עקומות" icon={faChartLine} onClick={() => setDataShown('GRAPHS')} isActive={dataShown === 'GRAPHS'} />
        <FooterItem label="מעקב" icon={faCalendarCheck} onClick={() => setDataShown('FOLLOW_UP')} isActive={dataShown === 'FOLLOW_UP'} />
        {hasProtocolFiles && (
          <FooterItem label="פרוטוקולים" icon={faFilePdf} onClick={() => setDataShown('PROTOCOLS')} isActive={dataShown === 'PROTOCOLS'} />
        )}
      </Footer>
    </div>
  );
};

// --- Sub-views ---------------------------------------------------------------

const BulletList: React.FC<{ items: string[] }> = ({ items }) => (
  <ul className="risks-bullets">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

/**
 * The neurotoxicity factors (§7 table 1) — the list that picks the treatment
 * curve and drives the calculator's checkbox — under the note saying what it is
 * FOR, then the monitoring rules (§1), which change no threshold.
 *
 * "תדירות המעקב" is a `risks-header`, not a sub-heading: it used to sit under a
 * second list of jaundice risk factors, and now that the list is gone it must
 * read as its own section rather than as a continuation of the neurotoxicity
 * bullets above it. Those two things decide different questions and the screen
 * has to keep saying so.
 */
const RisksView: React.FC<{ settings: Aap2022Settings }> = React.memo(({ settings }) => (
  <div className="content-risks">
    <h4 className="risks-header">גורמי סיכון לנוירוטוקסיות של בילירובין (AAP 2022)</h4>
    <p className="risks-note">{settings.neurotoxicityRiskFactorsNote}</p>
    <BulletList items={settings.neurotoxicityRiskFactors} />

    {settings.monitoringFrequency.length > 0 && (
      <>
        <h4 className="risks-header">תדירות המעקב</h4>
        <BulletList items={settings.monitoringFrequency} />
      </>
    )}
  </div>
));
RisksView.displayName = 'RisksView';

/**
 * The stroke colours the AAP 2022 guideline uses in its own threshold figures,
 * read out of the vector content streams of `data/new-graphs-2026/AAP-hyperbili.pdf`
 * so these charts read like the source the clinician has on the shelf.
 *
 * The guideline assigns them by a curve's rank inside its own figure, not by
 * gestational age: figure 2 draws six curves (40, 39, 38, 37, 36, 35 weeks) and
 * uses all six colours top-down, while figures 3/5/6 draw four (>=38, 37, 36, 35)
 * and reuse the first four the same way. So the index here is the curve's
 * position in its own chart, highest thresholds first — which is why the week
 * lists below are kept in descending order.
 */
const AAP_CURVE_COLORS = ['#026461', '#FF7400', '#97003C', '#00AFAC', '#CB5B01', '#FC1874'];

const curveDataset = (
  week: number,
  risk: boolean,
  kind: AapThresholdKind,
  label: string,
  color: string
) => ({
  data: smoothCurvePoints(getAapCurve(week, risk, kind)),
  borderColor: color,
  label,
  pointRadius: 0,
  borderWidth: 2,
  ...SMOOTH_LINE,
});

const overviewOptions = (title: string, yMin: number, yMax: number) => ({
  responsive: true,
  aspectRatio: 1,
  plugins: {
    legend: { display: true, position: 'top' as const, labels: { usePointStyle: true, pointStyle: 'line' } },
    tooltip: {
      rtl: true,
      textDirection: 'rtl' as const,
      mode: 'index' as const,
      intersect: false,
      callbacks: {
        label: (ctx: any) => `${ctx.dataset.label}: ${tooltipValue(ctx).toFixed(1)}`,
      },
    },
    title: { display: true, text: title },
  },
  scales: {
    x: { type: 'linear' as const, title: { display: true, text: 'גיל בשעות' }, ticks: { stepSize: 24 }, min: 0, max: AAP_MAX_HOURS },
    y: { type: 'linear' as const, title: { display: true, text: 'בילירובין (mg/dl)' }, min: yMin, max: yMax, ticks: { stepSize: 2 } },
  },
});

const GraphsView: React.FC<{ settings: Aap2022Settings }> = React.memo(({ settings }) => {
  const charts = useMemo(() => {
    // Both lists run highest gestational age first, because AAP_CURVE_COLORS is
    // indexed by the curve's rank in its own figure — see the comment there.
    const allWeeks = [40, 39, 38, 37, 36, 35];
    const sharedWeeks = [38, 37, 36, 35]; // 38/39/40 share one curve
    const weekLabel = (w: number, shared: boolean) => (shared && w === 38 ? '38+ שבועות' : w === 40 ? '40+ שבועות' : `${w} שבועות`);
    const datasets = (weeks: number[], risk: boolean, kind: AapThresholdKind, shared: boolean) =>
      weeks.map((w, i) => curveDataset(w, risk, kind, weekLabel(w, shared), AAP_CURVE_COLORS[i]));
    return [
      {
        title: 'ספי טיפול באור - ללא גורמי סיכון (Fig. 2)',
        data: { datasets: datasets(allWeeks, false, 'phototherapy', false) },
        yMin: 4,
        yMax: 24,
      },
      {
        title: 'ספי טיפול באור - עם גורמי סיכון (Fig. 3)',
        data: { datasets: datasets(sharedWeeks, true, 'phototherapy', true) },
        yMin: 4,
        yMax: 20,
      },
      {
        title: 'ספי החלפת דם - ללא גורמי סיכון (Fig. 5)',
        data: { datasets: datasets(sharedWeeks, false, 'exchange', true) },
        yMin: 14,
        yMax: 28,
      },
      {
        title: 'ספי החלפת דם - עם גורמי סיכון (Fig. 6)',
        data: { datasets: datasets(sharedWeeks, true, 'exchange', true) },
        yMin: 12,
        yMax: 24,
      },
    ];
  }, []);

  return (
    <div className="content-risks">
      <h4 className="risks-header">עקומות AAP 2022</h4>
      <p style={{ textAlign: 'right', padding: '0 1rem', fontSize: '0.9rem', color: PAGE_FONT_COLOR }}>
        סף הסלמת הטיפול הוא {fmt(settings.escalationDeltaMgDl)} מ"ג/ד"ל מתחת לסף החלפת הדם. ערכי הספים בשעות 1-24 מלווים באי-ודאות רבה (קווים מנוקדים במקור).
      </p>
      {charts.map((c) => (
        <div key={c.title} style={{ marginBottom: '24px', backgroundColor: 'white', padding: '8px', borderRadius: '8px' }}>
          <Line options={overviewOptions(c.title, c.yMin, c.yMax)} data={c.data} />
        </div>
      ))}
    </div>
  );
});
GraphsView.displayName = 'GraphsView';

const FollowUpView: React.FC<{ settings: Aap2022Settings }> = React.memo(({ settings }) => (
  <div className="content-risks aap-follow-up">
    <h4 className="risks-header">מעקב לאחר שחרור - לפי ההפרש מסף טיפול באור</h4>
    <p className="aap-follow-up-note">
      חל על יילודים בגיל 12 שעות ומעלה שלא קיבלו טיפול באור באשפוז. ההפרש = סף טיפול באור לגיל היילוד פחות ערך הבילירובין שנמדד.
    </p>
    <table className="aap-follow-up-table">
      <thead>
        <tr>
          <th>הפרש מהסף (מ"ג/ד"ל)</th>
          <th>המלצה</th>
        </tr>
      </thead>
      <tbody>
        {settings.followUpBands.map((band, i) => (
          <tr key={i}>
            <td style={{ whiteSpace: 'nowrap' }}>
              {band.maxDelta === null ? `≥${fmt(band.minDelta)}` : `${fmt(band.minDelta)} - ${fmt(band.maxDelta)}`}
            </td>
            <td>{band.recommendation}</td>
          </tr>
        ))}
      </tbody>
    </table>
    <p className="aap-follow-up-note">{settings.followUpBefore12hText}</p>
    {settings.followUpNotes.length > 0 && (
      <ul className="list-group list-group-flush risks-container">
        {settings.followUpNotes.map((note, i) => (
          <li key={i} className="list-group-item">{note}</li>
        ))}
      </ul>
    )}

    <h4 className="risks-header">מעקב במהלך טיפול באור ולאחריו</h4>
    <ul className="list-group list-group-flush risks-container">
      <li className="list-group-item">
        הפסקת טיפול באור: כאשר TSB ירד לפחות {fmt(settings.stopPhototherapyDeltaMgDl)} מ"ג/ד"ל מתחת לסף הטיפול המתאים לגיל היילוד בעת ההחלטה.
      </li>
      {settings.postPhototherapyNotes.map((note, i) => (
        <li key={i} className="list-group-item">{note}</li>
      ))}
    </ul>
  </div>
));
FollowUpView.displayName = 'FollowUpView';

/**
 * The department's documents, one download tile each — whatever the hospital
 * put on the "פרוטוקולים" tab of the admin page. Out of the box that is the AAP
 * guideline the curves come from, bundled with the app; everything else is an
 * upload served from storage. A tile is one line, the title the admin gave it.
 *
 * The screen is only reachable when the list is non-empty (the footer item is
 * not rendered otherwise), so there is no empty state to write.
 */
const ProtocolsView: React.FC<{ files: ProtocolFile[] }> = React.memo(({ files }) => (
  <div className="content-risks">
    <h4 className="risks-header">פרוטוקולים להורדה</h4>
    <div className="aap-protocol-list">
      {files.map((doc, i) => {
        const href = protocolFileHref(doc);
        if (!href) return null;
        return (
          <a
            key={doc.path || doc.builtin || i}
            className="aap-protocol-item"
            href={href}
            download={protocolFileDownloadName(doc)}
            target="_blank"
            rel="noopener noreferrer"
          >
            <span className="aap-protocol-icon" aria-hidden="true">
              <FontAwesomeIcon icon={faFileArrowDown} />
            </span>
            <span className="aap-protocol-title">{doc.title}</span>
          </a>
        );
      })}
    </div>
  </div>
));
ProtocolsView.displayName = 'ProtocolsView';

export default PhototherapyAap2022;
